import { useCallback, useEffect, useState } from 'react';
import { fetchFactorDataSyncPreferences } from '../../../utils/ApiCall';

export default function useFactorDataSyncPreferences(userUid, onError) {
    const [preference, setPreference] = useState(null);
    useEffect(() => {
        let active = true;
        let loaded = false;
        let reading = false;
        let errorReported = false;
        let retryTimer;
        setPreference(null);
        if (!userUid) return undefined;
        const refresh = async () => {
            if (!active || loaded || reading) return;
            window.clearTimeout(retryTimer);
            reading = true;
            try {
                const result = await fetchFactorDataSyncPreferences();
                if (typeof result?.enabled !== 'boolean') throw new Error('Invalid sync preference.');
                if (active) {
                    loaded = true;
                    setPreference({ userUid, enabled: result.enabled });
                }
            } catch {
                if (active) {
                    if (!errorReported) {
                        onError('Could not load Factor data auto-sync. Automatic factor updates are paused while we retry.');
                        errorReported = true;
                    }
                    retryTimer = window.setTimeout(refresh, 60000);
                }
            } finally {
                reading = false;
            }
        };
        const onVisible = () => { if (document.visibilityState === 'visible') void refresh(); };
        void refresh();
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            active = false;
            window.clearTimeout(retryTimer);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [onError, userUid]);
    const setEnabled = useCallback((enabled) => setPreference({ userUid, enabled }), [userUid]);
    return {
        enabled: Boolean(userUid && preference?.userUid === userUid && preference.enabled),
        ready: Boolean(userUid && preference?.userUid === userUid),
        setEnabled,
    };
}
